// 自分のポインタ位置を viewer.sync として一定間隔で送信する hook。
// 30Hz だと API Gateway の課金が重いため 10Hz（100ms）に制限する。

import { useEffect, useRef } from "react";
import { useRealtimeClient } from "../../realtime/realtime-context";
import { useCurrentActor, type CurrentActor } from "../../auth/use-current-actor";
import {
  CANVAS_HEIGHT_PX,
  CANVAS_WIDTH_PX,
  screenToStage,
} from "../../lib/canvas-coordinates";
import { useCanvasStore } from "./store/canvas-store";
import type { RealtimeClient } from "../../realtime/realtime-client";
import type { Viewer } from "@refinement-canvas/domain";

const BROADCAST_INTERVAL_MS = 100;

export function useCursorBroadcaster(): void {
  const client = useRealtimeClient();
  const actor = useCurrentActor();
  const selectedObjectIds = useCanvasStore((s) => s.selectedObjectIds);
  const lastSentRef = useRef<number>(0);
  const pendingRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    // 初期表示と選択変更を viewer.sync（カーソル無し）として共有する。
    // WebSocket 接続前に発火してもクライアント側キューで送信されるため、
    // 参加者一覧と他者選択枠の初期同期を落とさない。
    sendViewerSync(client, actor, selectedObjectIds);
  }, [actor, client, selectedObjectIds]);

  useEffect(() => {
    const onPointerMove = (event: PointerEvent): void => {
      const view = useCanvasStore.getState().view;
      const stagePoint = screenToStage({ x: event.clientX, y: event.clientY }, view);
      const xRatio = clamp01(stagePoint.x / CANVAS_WIDTH_PX);
      const yRatio = clamp01(stagePoint.y / CANVAS_HEIGHT_PX);
      pendingRef.current = { x: xRatio, y: yRatio };
      flushIfDue();
    };

    const flushIfDue = (): void => {
      const now = Date.now();
      if (now - lastSentRef.current < BROADCAST_INTERVAL_MS) return;
      const pending = pendingRef.current;
      if (!pending) return;
      lastSentRef.current = now;
      pendingRef.current = null;
      sendViewerSync(client, actor, selectedObjectIds, {
        xRatio: pending.x,
        yRatio: pending.y,
      });
    };

    window.addEventListener("pointermove", onPointerMove);
    const intervalId = window.setInterval(flushIfDue, BROADCAST_INTERVAL_MS);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.clearInterval(intervalId);
    };
  }, [actor, client, selectedObjectIds]);
}

function clamp01(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function sendViewerSync(
  client: RealtimeClient,
  actor: CurrentActor,
  selectedObjectIds: readonly string[],
  cursor?: Viewer["cursor"]
): void {
  client.sendMessage({
    type: "viewer.sync",
    viewer: {
      userId: actor.userId,
      displayName: actor.displayName,
      color: actor.color,
      avatarImageUrl: actor.avatarImageUrl,
      selectedObjectIds,
      ...(cursor ? { cursor } : {}),
    },
  });
}
